<script lang="ts" setup>
import routes from '@/data/navigation-routes'
import type { NavigationTab, NavigationTabPayload } from '@/types'

interface Props {
  navbarColor?: string
  isTransparent?: boolean
  buttonsColor?: '#000033' | '#fff'
}
const props = defineProps<Props>()

const {
  isTabSelected,
  changeTabState,
  setTabIndex,
  COMPONENT_ATTRS,
  currentIndex,
  selectedTab,
  isLastTab,
  isFirstTab,
  isOpen,
} = useTabs(routes, 'navigation')

const { locale } = useI18n()

const isGermanRoute = computed(() => locale.value === 'de')

const offsetX = ref('0px')
const activeTab = computed<NavigationTab>(
  () => selectedTab.value ?? routes[0]!,
)

const handleChangeState = ({ offset, event }: NavigationTabPayload) => {
  if (offset) {
    offsetX.value = offset
  }
  changeTabState(event)
}

const handleSkipNavigation = () => {
  const element = document.getElementById('main-content')
  element?.focus()
}

const closeNavigationPanel = (e: MouseEvent) => {
  if (!e?.relatedTarget) {
    return changeTabState({
      type: 'CLOSE',
    })
  }

  const floatingPromo = document.getElementById('floating-promo') as Element
  if (floatingPromo && floatingPromo.contains(e.relatedTarget as Element)) {
    return changeTabState({
      type: 'RESET',
    })
  }
}
const localePath = useLocalePath()

const {
  navbarStyle,
  isNavbarTransparent,
  isNavbarLight,
  isConfiguredTransparent,
} = useNavbarStyle(
  toRef(props, 'navbarColor'),
  toRef(props, 'isTransparent'),
  toRef(props, 'buttonsColor'),
)
</script>

<template>
  <nav
    id="desktop-navigation"
    v-click-outside="closeNavigationPanel"
    :class="[
      'navigation',
      {
        'navigation--transparent': isConfiguredTransparent,
        'navigation--scrolled': isConfiguredTransparent && !isNavbarTransparent,
      },
    ]"
    :style="navbarStyle"
    @mouseleave="closeNavigationPanel"
  >
    <button
      type="button"
      class="navigation__hidden-link"
      @click="handleSkipNavigation"
    >
      Skip Navigation
    </button>
    <header class="navigation__header">
      <NavigationLogo
        class="navigation__logo"
        :is-white="isNavbarLight"
        :is-transparent="isNavbarTransparent"
        @mouseenter="changeTabState({ type: 'RESET' })"
      />
      <section class="navigation__tabs" aria-multiselectable="false">
        <ul id="navigation-tab-list" class="navigation__tabs" role="tablist">
          <NavigationHeaderItem
            v-for="(tab, index) in routes"
            :key="tab.tabName"
            :tab-name="tab.tabName"
            :data-target="COMPONENT_ATTRS.TAB_ITEM"
            :tab="tab"
            :index="index"
            :tab-index="setTabIndex(index)"
            :is-selected="isTabSelected(index)"
            :is-last-item="isLastTab"
            @change-tab-state="handleChangeState"
          />
        </ul>

        <NavigationTabPanel
          :is-open="isOpen"
          :offset-x="offsetX"
          :current-tab-index="currentIndex"
          :current-tab="activeTab"
          :data-test-id="`test-index-${currentIndex}`"
          :class="{
            'navigation-tab-panel--last': isLastTab,
            'navigation-tab-panel--first': isFirstTab,
            'navigation-tab-panel--de': isGermanRoute,
          }"
          @change-tab-state="handleChangeState"
        />
      </section>
      <div class="navigation__right">
        <NavigationLanguageSwitcher
          :navbarColor="navbarColor"
          :is-transparent="isTransparent"
          :buttons-color="buttonsColor"
          class="navigation__lang-switcher"
          :tabindex="currentIndex === routes.length - 1 ? 0 : -1"
          @focus="changeTabState({ type: 'RESET' })"
          @mouseenter="changeTabState({ type: 'RESET' })"
        />
        <BaseButton
          id="navigation-cta-button"
          data-target="true"
          class="navigation__cta-button"
          small
          :title="$t('common.navigation.cta')"
          :to="localePath('/contact-sales')"
          @focus="changeTabState({ type: 'RESET' })"
          @mouseenter="changeTabState({ type: 'RESET' })"
        >
          <template #text>
            {{ $t('common.navigation.cta') }}
          </template>
        </BaseButton>
      </div>
    </header>
  </nav>
</template>

<style lang="scss" scoped>
.navigation {
  --space: 16px;
  --navbar-morph: background-color 0.35s ease, color 0.35s ease,
    box-shadow 0.35s ease;
  transition: var(--navbar-morph);
  will-change: background-color;
  position: sticky;
  z-index: 9998;
  top: 0;
  display: none;
  height: var(--header-height);
  background-color: var(--navbar-solid-bg, var(--white));
  color: var(--navbar-solid-text, var(--cobalt-800));
  width: 100%;

  &--transparent:not(&--scrolled) {
    background-color: transparent;
    color: var(--navbar-overlay-text, var(--white));
  }

  &--transparent {
    margin-bottom: calc(-1 * var(--header-height));
  }

  &--scrolled {
    @include shadow-flat-soft;
  }

  @media (min-width: $breakpoint-lg) {
    display: flex;
  }
  &__logo {
    width: 214px;
    justify-self: start;
  }

  &__hidden-link {
    position: absolute;
    background-color: rgba(white, 0.6);
    padding: 8px;
    z-index: 9999;
    top: 50%;
    transform: translate(-120%, -50%);
    transition: transform 150ms ease-in;

    &:focus {
      transform: translate(0, -50%);
    }
  }

  &__header {
    width: 100%;
    height: var(--header-height);
    font-size: var(--font-size-paragraph-s);
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    padding: 0 24px;
  }

  &__tabs {
    display: flex;
    align-items: center;
    word-break: normal;
  }

  &__cta-button {
    display: block;
    flex-shrink: 0;
    margin-left: 22px;
  }

  &__right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    justify-self: end;
  }
}
</style>
